import React from "react";
import { motion } from "framer-motion";

const formatAppointment = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

const PrescriptionPreview = ({
  patientName,
  date,
  symptoms,
  diagnosis,
  medicines,
  labTests,
  sos,
  notes,
  nextAppointment,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl shadow p-4 border border-blue-100 text-sm sm:text-base"
    >
      <h2 className="text-lg sm:text-xl font-bold text-blue-700 text-center mb-4">
        🩺 Digital Prescription
      </h2>

      {/* Patient Info and SOS */}
      <div className="bg-blue-100 rounded-lg p-4 mb-4 text-gray-800">
        <p className="font-semibold">
          👤 <span className="text-blue-700">Patient Name:</span> {patientName || "N/A"}
        </p>
        <p className="mt-1">
          📅 <span className="text-blue-700">Date:</span> {date || "N/A"}
        </p>
        {sos && (
          <p className="mt-2 bg-blue-200 text-blue-800 p-2 rounded">
            🚨 <span className="font-semibold">SOS:</span> {sos}
          </p>
        )}
      </div>

      <div className="border-t border-gray-200 mb-3"></div>

      {/* Symptoms */}
      <div className="mb-3">
        <h3 className="font-semibold text-blue-600">🩹 Symptoms</h3>
        <p className="text-gray-800">{symptoms || "N/A"}</p>
      </div>

      {/* Diagnosis */}
      <div className="mb-3">
        <h3 className="font-semibold text-blue-600">🧠 Diagnosis</h3>
        <p className="text-gray-800">{diagnosis || "N/A"}</p>


      </div>

      {/* Medicines Table */}
      <div className="mb-3">
        {/* <h3 className="font-semibold text-blue-600 mb-1">💊 Medicines</h3> */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-blue-50 rounded-lg overflow-hidden text-xs sm:text-sm">
            <thead className="bg-blue-100">
              <tr>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Dosage</th>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">Days</th>
              </tr>
            </thead>
            <tbody>
              {medicines && medicines.length > 0 ? (
                medicines.map((med, index) => (
                  <tr key={index} className="border-t">
                    <td className="p-2">{med.name || med.medicine_name || "-"}</td>
                    <td className="p-2">{med.dosage || "-"}</td>
                    <td className="p-2">
                      {
                        Array.isArray(med.reminder_times) && med.reminder_times.length > 0
                          ? med.reminder_times.join(", ")
                          : Array.isArray(med.times) && med.times.length > 0
                            ? med.times.join(", ")
                            : med.reminder_times || med.times || "-"
                      }


                    </td>
                    <td className="p-2">
                      {
                        med.duration_value
                          ? `${med.duration_value} ${Array.isArray(med.duration_unit)
                            ? med.duration_unit.join(", ")
                            : med.duration_unit || "-"
                          }`
                          : med.reminderDays
                            ? med.reminderDays
                            : Array.isArray(med.duration_unit)
                              ? med.duration_unit.join(", ")
                              : med.duration_unit || "-"
                      }
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-2 text-center">
                    No medicines prescribed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lab Tests */}
      <div className="mb-3">
        <h3 className="font-semibold text-blue-600">🧪 Lab Tests</h3>
        <ul className="list-disc list-inside text-gray-800">
        {labTests && labTests.filter(Boolean).length > 0 ? (
  labTests.map((test, index) => {
    let testObj = test;

    // If test is a string, parse it
    if (typeof test === "string") {
      try {
        testObj = JSON.parse(test.replace(/'/g, '"'));
      } catch (e) {
        console.error("Invalid test string:", test);
        testObj = { name: test };
      }
    }

    // If testObj.name is a stringified object, parse it too
    if (typeof testObj.name === "string" && testObj.name.trim().startsWith("{")) {
      try {
        testObj = JSON.parse(testObj.name.replace(/'/g, '"'));
      } catch (e) {
        console.error("Invalid name string:", testObj.name);
      }
    }

    return <li key={index}>{testObj.name}</li>;
  })
) : (
  <li>No lab tests recommended.</li>
)}



        </ul>
      </div>

      {/* Additional Notes */}
      {notes && (
        <div className="mb-3">
          <h3 className="font-semibold text-blue-600">📝 Additional Notes</h3>
          <p className="text-gray-800">{notes}</p>
        </div>
      )}

      {/* Next Appointment */}
      {nextAppointment && (
        <div>
          <h3 className="font-semibold text-blue-600">📆 Next Appointment</h3>
          <p className="text-gray-800">{formatAppointment(nextAppointment)}</p>
        </div>
      )}
    </motion.div>
  );
};

export default PrescriptionPreview;
